import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Save, Wand2, ImageIcon } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { api, errMsg, fileUrl, uploadImage } from "@/lib/api";
import Avatar3D, { colorsFromItems } from "@/components/Avatar3D";
import { Page } from "@/components/Page";
import { Loader } from "@/components/AuthGuards";

const SKINS = ["#F3D9C4", "#E8BE9A", "#D9A882", "#B97F57", "#8D5A3B", "#5C3A24"];
const MEASURES = [["height", "Körpergröße", 140, 210], ["chest", "Brust", 70, 140], ["waist", "Taille", 55, 140], ["hip", "Hüfte", 70, 150]];

export default function Studio() {
  const [av, setAv] = useState(null);
  const [outfits, setOutfits] = useState([]);
  const [sel, setSel] = useState("");
  const [tryons, setTryons] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/avatar").then(({ data }) => setAv(data));
    api.get("/outfits").then(({ data }) => setOutfits(data));
    api.get("/tryon").then(({ data }) => setTryons(data));
  }, []);

  if (!av) return <Loader />;
  const outfit = outfits.find((o) => o.id === sel);
  const set = (k, v) => setAv({ ...av, [k]: v });

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy({ photo: true });
    try {
      const path = await uploadImage(f);
      const next = { ...av, photo_path: path };
      setAv(next);
      await api.put("/avatar", next);
      toast.success("Foto gespeichert");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy({});
  };

  const save = async () => {
    setBusy({ save: true });
    try { await api.put("/avatar", av); toast.success("Avatar gespeichert"); } catch (err) { toast.error(errMsg(err)); }
    setBusy({});
  };

  const runTryon = async () => {
    if (!outfit) return toast.error("Wähle zuerst ein gespeichertes Outfit");
    setBusy({ tryon: true });
    try {
      const { data } = await api.post("/tryon", { items: outfit.items }, { timeout: 180000 });
      setTryons([data, ...tryons]);
      toast.success("Deine KI-Anprobe ist fertig");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy({});
  };

  return (
    <Page over="Avatar Studio" title="Dein persönlicher Avatar" sub="Lade ein Foto von dir hoch, stelle deine Maße ein und probiere gespeicherte Outfits an – als 3D-Figur oder realistisches KI-Bild." testid="studio-page">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="surface relative h-[560px] overflow-hidden lg:col-span-7">
          <Avatar3D measurements={av} colors={colorsFromItems(outfit?.resolved)} photoUrl={fileUrl(av.photo_path)} />
          <div className="absolute left-4 top-4 flex items-center gap-2">
            <select value={sel} onChange={(e) => setSel(e.target.value)} className="h-10 rounded-full border border-white/10 bg-black/70 px-4 text-sm backdrop-blur" data-testid="studio-outfit-select">
              <option value="">Basis-Look</option>
              {outfits.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-5">
          <div className="surface p-6">
            <p className="label-over">1 · Dein Foto</p>
            <div className="mt-4 flex items-center gap-5">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0B]">
                {av.photo_path ? <img src={fileUrl(av.photo_path)} alt="Du" className="h-full w-full object-cover" data-testid="avatar-photo-preview" /> : <ImageIcon className="m-8 h-8 w-8 text-neutral-600" />}
              </div>
              <label className="btn-ghost cursor-pointer" data-testid="avatar-upload-label">
                <Upload className="h-4 w-4" /> {busy.photo ? "Lädt…" : av.photo_path ? "Foto ändern" : "Foto hochladen"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhoto} data-testid="avatar-upload-input" />
              </label>
            </div>
            <p className="mt-3 text-xs text-neutral-500">Am besten ein gut beleuchtetes Ganzkörperfoto von vorne. Nur du kannst es sehen.</p>
          </div>

          <div className="surface space-y-5 p-6">
            <p className="label-over">2 · Körper & Maße</p>
            <div className="flex gap-2">
              {["feminin", "maskulin"].map((b) => (
                <button key={b} onClick={() => set("body_type", b)} className={`flex-1 rounded-full border py-2 text-sm capitalize transition-colors ${av.body_type === b ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-neutral-400"}`} data-testid={`body-type-${b}`}>{b}</button>
              ))}
            </div>
            {MEASURES.map(([k, l, min, max]) => (
              <div key={k}>
                <div className="mb-2 flex justify-between text-sm"><span className="text-neutral-400">{l}</span><span className="font-mono" data-testid={`measure-value-${k}`}>{av[k]} cm</span></div>
                <Slider value={[av[k]]} min={min} max={max} step={1} onValueChange={([v]) => set(k, v)} data-testid={`measure-slider-${k}`} />
              </div>
            ))}
            <div>
              <span className="text-sm text-neutral-400">Hautton</span>
              <div className="mt-2 flex gap-2">
                {SKINS.map((c) => (
                  <button key={c} onClick={() => set("skin", c)} style={{ background: c }} className={`h-8 w-8 rounded-full ring-offset-2 ring-offset-[#121215] transition-transform hover:scale-110 ${av.skin === c ? "ring-2 ring-[#D4AF37]" : ""}`} data-testid={`skin-${c.slice(1)}`} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-neutral-400">Konfektionsgröße
                <select value={av.size} onChange={(e) => set("size", e.target.value)} className="mt-2 h-10 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-3 text-[#FAF7F0]" data-testid="avatar-size-select">
                  {["XS", "S", "M", "L", "XL", "XXL"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-sm text-neutral-400">Stil-Vorlieben
                <input value={av.style} onChange={(e) => set("style", e.target.value)} placeholder="z.B. minimal, Streetwear" className="mt-2 h-10 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-3 text-[#FAF7F0]" data-testid="avatar-style-input" />
              </label>
            </div>
            <button onClick={save} disabled={busy.save} className="btn-cream w-full" data-testid="avatar-save-button"><Save className="h-4 w-4" /> Avatar speichern</button>
          </div>
        </div>
      </div>

      <section className="mt-14">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="label-over">3 · KI-Anprobe</p>
            <h2 className="mt-2 font-serif text-3xl">Sieh dich realistisch im Outfit</h2>
            <p className="mt-2 text-sm text-neutral-400">Wähle oben ein gespeichertes Outfit und lass die KI ein Bild von dir darin erzeugen (dauert ca. 20–40 Sek.).</p>
          </div>
          <button onClick={runTryon} disabled={busy.tryon || !av.photo_path} className="btn-cream" data-testid="tryon-start-button">
            <Wand2 className={`h-4 w-4 ${busy.tryon ? "animate-spin" : ""}`} /> {busy.tryon ? "KI erstellt dein Bild…" : "KI-Anprobe starten"}
          </button>
        </div>
        {!av.photo_path && <p className="mt-3 text-xs text-amber-200/70">Lade zuerst ein Foto hoch.</p>}
        {outfits.length === 0 && <p className="mt-3 text-xs text-neutral-500">Noch keine Outfits gespeichert – stelle im Outfit Builder eins zusammen.</p>}
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="tryon-gallery">
          {tryons.map((t) => (
            <a key={t.id} href={fileUrl(t.image_path)} target="_blank" rel="noreferrer" className="card-lux overflow-hidden" data-testid={`tryon-image-${t.id}`}>
              <img src={fileUrl(t.image_path)} alt="KI-Anprobe" className="aspect-[3/4] w-full object-cover" />
            </a>
          ))}
        </div>
      </section>
    </Page>
  );
}
